import { useMemo, useState } from 'react';
import { CircularProgress, Divider, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from '../../../config/dayjs';
import { getNotificationTypeMeta } from '../../../common/utils/notificationTypeMeta';
import {
  useNotificationsQuery,
  useMarkAsRead,
  useMarkAllAsRead,
} from '../../../hooks/useNotifications';
import {
  AppNotification,
  NotificationTypeEnum,
  NotificationsQueryFilters,
} from '../../../types/notification';
import { BroadcastForm } from './BroadcastForm';
import { MobileBottomNav } from '../Stations/MobileBottomNav';
import { EmptyState } from '../../../components/common/EmptyState/EmptyState';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';
import { Pagination } from '../../../components/common/Pagination';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants/pagination';
import { getDateRangeFromPreset } from '../../../shared/utils/dateRangeUtils';
import { useListPage } from '../../../hooks/useListPage';
import { DashboardQueryKey } from '../../../constants/dashboardQueryKeys';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSION } from '../../../shared/utils/permissions';

const typeLabelKey: Record<NotificationTypeEnum, string> = {
  [NotificationTypeEnum.CHARGING_PREPARING]: 'dashboard.notifications.preparing',
  [NotificationTypeEnum.CHARGING_STARTED]: 'dashboard.notifications.started',
  [NotificationTypeEnum.CHARGING_FINISHED]: 'dashboard.notifications.finished',
  [NotificationTypeEnum.CHARGING_DOWNTIME]: 'dashboard.notifications.downtime',
  [NotificationTypeEnum.STATION_DISCONNECTED]: 'dashboard.notifications.stationDisconnected',
  [NotificationTypeEnum.SYSTEM_NEWS]: 'dashboard.notifications.systemNews',
};

const TYPE_VALUES = Object.values(NotificationTypeEnum);

const DATE_PRESETS = ['Any', 'Today', 'Last 7 days', 'Last 30 days'] as const;
type DatePreset = (typeof DATE_PRESETS)[number];

const datePresetKey: Record<DatePreset, string> = {
  Any: 'dashboard.notifications.datePresetAny',
  Today: 'dashboard.notifications.datePresetToday',
  'Last 7 days': 'dashboard.notifications.datePreset7days',
  'Last 30 days': 'dashboard.notifications.datePreset30days',
};

interface NotificationsFilterState {
  tab: 'all' | 'unread';
  types: NotificationTypeEnum[];
  datePreset: DatePreset;
}

const INITIAL_FILTERS: NotificationsFilterState = {
  tab: 'all',
  types: [],
  datePreset: 'Any',
};

function NotificationRow({
  notification,
  onRead,
}: {
  notification: AppNotification;
  onRead: (id: string) => void;
}) {
  const isUnread = !notification.read_at;
  const plainMessage = notification.message.replace(/<[^>]*>/g, '').replace(/\n/g, ' · ');
  const typeMeta = getNotificationTypeMeta(notification.type);

  return (
    <div
      className={`notif-row${isUnread ? ' unread' : ''}`}
      onClick={() => isUnread && onRead(notification.id)}
    >
      <div className="notif-row-icon" style={{ background: typeMeta.color + '18' }}>
        <span className="material-symbols-outlined" style={{ color: typeMeta.color }}>
          {typeMeta.icon}
        </span>
      </div>

      <div className="notif-row-body">
        <div className="notif-row-top">
          <span className="notif-row-title">{notification.title}</span>
          <span className="notif-row-time">{dayjs(notification.created_at).fromNow()}</span>
        </div>
        <p className="notif-row-message">{plainMessage}</p>
      </div>

      {isUnread && <div className="notif-row-unread-dot" />}
    </div>
  );
}

/** Label + chip row using the app-wide `filter-chip` styling. */
function ChipRow({
  label,
  options,
  isActive,
  onToggle,
}: {
  label: string;
  options: readonly { value: string; chipLabel: string }[];
  isActive: (value: string) => boolean;
  onToggle: (value: string) => void;
}) {
  return (
    <div className="notif-chips-row" role="toolbar" aria-label={label}>
      <span className="notif-chips-label">{label}</span>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`filter-chip ${isActive(option.value) ? 'active' : ''}`}
          onClick={() => onToggle(option.value)}
          aria-pressed={isActive(option.value)}
        >
          {option.chipLabel}
        </button>
      ))}
    </div>
  );
}

export const NotificationsV2 = () => {
  const { t } = useTranslation();
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const { has } = usePermissions();
  const canBroadcast = has(PERMISSION.NOTIFICATIONS_MANAGE);

  const {
    page,
    rowsPerPage,
    filters,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    refresh,
  } = useListPage<NotificationsFilterState>({
    initialFilters: INITIAL_FILTERS,
    queryKeyRoot: DashboardQueryKey.NOTIFICATIONS,
    initialRowsPerPage: 25,
    urlSync: true,
    arrayFilterKeys: ['types'],
  });

  // All filters run server-side so `total` (and pagination) reflect the
  // filtered set across every page, not just the fetched one.
  const serverFilters = useMemo<NotificationsQueryFilters>(() => {
    const dateBounds =
      filters.datePreset === 'Any' ? {} : getDateRangeFromPreset(filters.datePreset);
    return {
      unreadOnly: filters.tab === 'unread' ? true : undefined,
      types: filters.types.length > 0 ? [...filters.types].sort() : undefined,
      startDate: dateBounds.startDate,
      endDate: dateBounds.endDate,
    };
  }, [filters]);

  const { data, isLoading, error } = useNotificationsQuery(
    page + 1,
    rowsPerPage,
    serverFilters,
  );
  const { mutate: markAsRead } = useMarkAsRead();
  const { mutate: markAllAsRead, isPending: isMarkingAll } = useMarkAllAsRead();

  const total = data?.total ?? 0;
  const unreadCount = data?.unreadCount ?? 0;
  const notifications = data?.data ?? [];

  const toggleType = (value: string) => {
    const type = value as NotificationTypeEnum;
    handleFilterChange({
      types: filters.types.includes(type)
        ? filters.types.filter((existing) => existing !== type)
        : [...filters.types, type],
    });
  };

  const filterCount = filters.types.length + (filters.datePreset !== 'Any' ? 1 : 0);

  const filterChips = (
    <>
      <ChipRow
        label={t('dashboard.notifications.filterTypeLabel')}
        options={TYPE_VALUES.map((value) => ({ value, chipLabel: t(typeLabelKey[value]) }))}
        isActive={(value) => filters.types.includes(value as NotificationTypeEnum)}
        onToggle={toggleType}
      />
      <ChipRow
        label={t('dashboard.notifications.filterDateLabel')}
        options={DATE_PRESETS.map((preset) => ({
          value: preset,
          chipLabel: t(datePresetKey[preset]),
        }))}
        isActive={(value) => filters.datePreset === value}
        onToggle={(value) => handleFilterChange({ datePreset: value as DatePreset })}
      />
    </>
  );

  const list = error ? (
    <ErrorMessage
      message={
        error instanceof Error ? error.message : t('dashboard.notifications.errorLoading')
      }
      onRetry={refresh}
    />
  ) : isLoading ? (
    <div className="notif-loading">
      <CircularProgress size={28} sx={{ color: '#10b981' }} />
    </div>
  ) : notifications.length === 0 ? (
    <EmptyState
      icon="notifications_off"
      message={
        filterCount > 0
          ? t('dashboard.notifications.noNotificationsFiltered')
          : t('dashboard.notifications.noNotifications')
      }
    />
  ) : (
    notifications.map((n, i) => (
      <div key={n.id}>
        <NotificationRow notification={n} onRead={markAsRead} />
        {i < notifications.length - 1 && <Divider />}
      </div>
    ))
  );

  const pagination = total > DEFAULT_ROWS_PER_PAGE && (
    <>
      <Divider />
      <Pagination
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </>
  );

  const tabsBar = (
    <div className="notif-tabs">
      <Tabs
        value={filters.tab}
        onChange={(_, v) => handleFilterChange({ tab: v })}
        sx={{
          px: 2,
          minHeight: 44,
          '& .MuiTab-root': { fontSize: 13, minHeight: 44, textTransform: 'none', fontWeight: 500 },
          '& .Mui-selected': { color: '#059669' },
          '& .MuiTabs-indicator': { background: '#059669' },
        }}
      >
        <Tab value="all" label={t('dashboard.notifications.tabAll')} />
        <Tab
          value="unread"
          label={
            unreadCount > 0
              ? t('dashboard.notifications.tabUnreadWithCount', { count: unreadCount })
              : t('dashboard.notifications.tabUnread')
          }
        />
      </Tabs>
    </div>
  );

  const markAllBtn = unreadCount > 0 && (
    <button
      type="button"
      className="notif-mark-all-btn"
      onClick={() => markAllAsRead()}
      disabled={isMarkingAll}
    >
      <span className="material-symbols-outlined">done_all</span>
      {t('dashboard.notifications.markAllRead')}
    </button>
  );

  return (
    <>
      {/* Desktop Layout */}
      <div className="desktop-layout">
        <div className="notif-desktop-wrap">
          {/* The page title is the dashboard header's; this row only carries
              the unread count and its action. */}
          {unreadCount > 0 && (
            <div className="notif-page-header">
              <span className="notif-unread-note">
                {t('dashboard.notifications.unreadCount', { count: unreadCount })}
              </span>
              {markAllBtn}
            </div>
          )}

          {canBroadcast && <BroadcastForm />}

          <div className="notif-chips-stack">{filterChips}</div>

          <div className="notif-card">
            {tabsBar}
            {list}
            {pagination}
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="mobile-layout">
        <header className="mobile-header">
          <div className="mobile-header-top">
            <h1 className="mobile-header-title">{t('dashboard.notifications.title')}</h1>
            {markAllBtn}
          </div>
          {unreadCount > 0 && (
            <span className="notif-unread-note">
              {t('dashboard.notifications.unreadCount', { count: unreadCount })}
            </span>
          )}
        </header>

        <main className="mobile-content">
          {/* Collapsible broadcast for admins */}
          {canBroadcast && (
            <>
              <button
                type="button"
                className="notif-broadcast-toggle"
                onClick={() => setBroadcastOpen((p) => !p)}
                aria-expanded={broadcastOpen}
              >
                <span className="material-symbols-outlined" aria-hidden="true">campaign</span>
                <span>{t('dashboard.notifications.broadcast')}</span>
                <span
                  className="material-symbols-outlined notif-broadcast-toggle-chevron"
                  aria-hidden="true"
                  style={{ transform: broadcastOpen ? 'rotate(180deg)' : 'none' }}
                >
                  expand_more
                </span>
              </button>
              {broadcastOpen && <BroadcastForm />}
            </>
          )}

          <div className="notif-chips-stack mobile">{filterChips}</div>

          <div className="notif-card">
            {tabsBar}
            {list}
            {pagination}
          </div>
        </main>

        <MobileBottomNav />
      </div>
    </>
  );
};
